import React, { useEffect, useState } from 'react';
import { getFieldDisplayName } from '../formSchemaUtils';
import { Icon } from './icons';
import TemplateBox from './TemplateBox';
import {
  PAGE_HEIGHT,
  PAGE_WIDTH,
  POINTS_PER_INCH,
  PRINT_MARGIN,
  detailLabel,
  drawnRect,
  movedRect,
  resizedRect,
  sameRect,
} from './templateLayout';

// Screen pixels a press has to travel before it is a drag rather than a click.
const DRAG_THRESHOLD_PX = 3;

const HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
const HANDLE_PLACES = {
  nw: 'left-0 top-0 cursor-nwse-resize',
  n: 'left-1/2 top-0 cursor-ns-resize',
  ne: 'left-full top-0 cursor-nesw-resize',
  e: 'left-full top-1/2 cursor-ew-resize',
  se: 'left-full top-full cursor-nwse-resize',
  s: 'left-1/2 top-full cursor-ns-resize',
  sw: 'left-0 top-full cursor-nesw-resize',
  w: 'left-0 top-1/2 cursor-ew-resize',
};

// Graph paper: a line every grid step, and a stronger one every inch so the
// sheet can be measured by eye.
const gridBackground = (grid, scale) => {
  if (!grid) return null;
  const minor = grid * scale;
  const major = POINTS_PER_INCH * scale;
  return {
    backgroundImage: [
      'linear-gradient(to right, rgba(139, 92, 246, 0.28) 1px, transparent 1px)',
      'linear-gradient(to bottom, rgba(139, 92, 246, 0.28) 1px, transparent 1px)',
      'linear-gradient(to right, rgba(148, 163, 184, 0.3) 1px, transparent 1px)',
      'linear-gradient(to bottom, rgba(148, 163, 184, 0.3) 1px, transparent 1px)',
    ].join(', '),
    backgroundSize: [
      `${major}px ${major}px`,
      `${major}px ${major}px`,
      `${minor}px ${minor}px`,
      `${minor}px ${minor}px`,
    ].join(', '),
  };
};

const placeStyle = (rect, scale) => ({
  left: rect.x * scale,
  top: rect.y * scale,
  width: rect.w * scale,
  height: rect.h * scale,
});

const boxName = (box, field) => {
  if (box.kind === 'text') return box.text ? `Text: ${box.text}` : 'Text box';
  if (box.kind === 'detail') return detailLabel(box.detail);
  if (!box.fieldId) return 'Empty box';
  return (field ? getFieldDisplayName(field) : box.fieldName) || 'Question';
};

// The sheet of paper and everything on it. Editing, it is graph paper you can
// work on directly: press on a box to select and drag it, pull a handle to
// resize it, press on empty paper and drag to draw a new box. Previewing, the
// grid and the outlines go, and the page reads the way it will print.
//
// A gesture lives here until it is dropped, and only then is reported up — one
// `onCommitRect` per move, one `onDraw` per box drawn — so the layout's history
// gets a step per gesture rather than one per pointer event. Holding Option
// (Alt) moves off the grid.
const TemplateCanvas = ({
  pageRef,
  boxes,
  fieldsById,
  issueById,
  selectedId,
  mode,
  scale,
  grid,
  sampleContext,
  dropPreview,
  onSelect,
  onCommitRect,
  onDraw,
  onOpenPicker,
}) => {
  const [gesture, setGesture] = useState(null);
  const editing = mode === 'edit';

  // Escape drops whatever is in hand and leaves the page as it was.
  useEffect(() => {
    if (!gesture) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setGesture(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [gesture]);

  const toPoint = (event) => {
    const bounds = pageRef.current.getBoundingClientRect();
    return { x: (event.clientX - bounds.left) / scale, y: (event.clientY - bounds.top) / scale };
  };

  const handlePointerDown = (event) => {
    if (!editing || event.button !== 0) return;
    const origin = toPoint(event);
    const handle = event.target.closest('[data-handle]')?.dataset.handle;
    const boxId = event.target.closest('[data-box-id]')?.dataset.boxId;
    const box = boxes.find((entry) => entry.id === (handle ? selectedId : boxId));
    if (box) {
      const start = { x: box.x, y: box.y, w: box.w, h: box.h };
      onSelect(box.id);
      setGesture({ type: handle ? 'resize' : 'move', id: box.id, handle, origin, start, rect: start, moved: false });
    } else {
      onSelect(null);
      setGesture({ type: 'draw', origin, rect: null, moved: false });
    }
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (!gesture) return;
    const point = toPoint(event);
    const dx = point.x - gesture.origin.x;
    const dy = point.y - gesture.origin.y;
    if (!gesture.moved && Math.hypot(dx, dy) * scale < DRAG_THRESHOLD_PX) return;
    const step = event.altKey ? 0 : grid;
    let rect;
    if (gesture.type === 'move') rect = movedRect(gesture.start, dx, dy, step);
    else if (gesture.type === 'resize') rect = resizedRect(gesture.start, gesture.handle, dx, dy, step);
    else rect = drawnRect(gesture.origin, point, step);
    setGesture((current) => current && { ...current, rect, moved: true });
  };

  const handlePointerUp = () => {
    const finished = gesture;
    setGesture(null);
    if (!finished?.moved || !finished.rect) return;
    if (finished.type === 'draw') onDraw(finished.rect);
    else if (!sameRect(finished.rect, finished.start)) onCommitRect(finished.id, finished.rect);
  };

  const pageWidth = PAGE_WIDTH * scale;
  const pageHeight = PAGE_HEIGHT * scale;
  const inch = POINTS_PER_INCH * scale;
  const drawing = gesture?.type === 'draw' && gesture.rect;

  return (
    <div className="relative inline-block pl-6 pt-5">
      {/* Inch rulers along the top and left edges of the sheet. */}
      <div className="absolute left-6 top-0 h-5 select-none" style={{ width: pageWidth }} aria-hidden="true">
        {Array.from({ length: 8 }, (_, i) => (
          <span key={i} className="absolute -translate-x-1/2 text-[10px] text-gray-500 tabular-nums" style={{ left: (i + 1) * inch }}>
            {i + 1}
          </span>
        ))}
      </div>
      <div className="absolute left-0 top-5 w-6 select-none" style={{ height: pageHeight }} aria-hidden="true">
        {Array.from({ length: 10 }, (_, i) => (
          <span key={i} className="absolute right-2 -translate-y-1/2 text-[10px] text-gray-500 tabular-nums" style={{ top: (i + 1) * inch }}>
            {i + 1}
          </span>
        ))}
      </div>

      <div
        ref={pageRef}
        role="group"
        aria-label="Template page, 8.5 by 11 inches"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={() => setGesture(null)}
        className={`relative bg-white shadow-2xl ring-1 ring-black/10 select-none touch-none ${editing ? 'cursor-crosshair' : ''}`}
        style={{ width: pageWidth, height: pageHeight }}
      >
        {editing && (
          <>
            <div className="absolute inset-0 pointer-events-none" style={gridBackground(grid, scale)} aria-hidden="true" />
            {/* Most printers can't reach the last quarter inch of the sheet. */}
            <div
              className="absolute pointer-events-none border border-dashed border-rose-300/80"
              style={{ inset: PRINT_MARGIN * scale }}
              aria-hidden="true"
            />
          </>
        )}

        {editing && boxes.length === 0 && !drawing && !dropPreview && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-10 text-center pointer-events-none">
            <span className="flex items-center justify-center w-14 h-14 rounded-2xl bg-purple-100 text-purple-600">
              <Icon name="blank" className="w-7 h-7" />
            </span>
            <p className="text-base font-semibold text-gray-700">Start laying out your page</p>
            <p className="max-w-xs text-sm text-gray-500">
              Drag a question here from the list on the left, or press anywhere on the paper and
              drag to draw a box, then choose what goes in it.
            </p>
          </div>
        )}

        {boxes.map((box) => {
          const live = gesture?.moved && gesture.id === box.id ? gesture.rect : box;
          const field = box.kind === 'field' ? fieldsById.get(box.fieldId) : null;
          const issue = issueById.get(box.id);
          const selected = editing && box.id === selectedId;
          let chrome = '';
          if (editing) {
            if (selected) chrome = 'outline outline-2 outline-purple-600 z-10';
            else if (issue) chrome = 'outline outline-1 outline-dashed outline-red-500 bg-red-50/70 hover:outline-2';
            else if (box.kind === 'text') chrome = 'outline outline-1 outline-dashed outline-gray-300 hover:outline-purple-500';
            else chrome = 'outline outline-1 outline-dashed outline-purple-400 bg-purple-50/80 hover:outline-purple-600';
          }
          const compact = live.w * scale < 40 || live.h * scale < 28;
          return (
            <div
              key={box.id}
              data-box-id={box.id}
              tabIndex={editing ? 0 : -1}
              role={editing ? 'button' : undefined}
              aria-label={editing ? `${boxName(box, field)}${issue ? ` — ${issue}` : ''}` : undefined}
              aria-pressed={editing ? selected : undefined}
              onFocus={editing ? () => onSelect(box.id) : undefined}
              onDoubleClick={editing ? () => onOpenPicker(box.id) : undefined}
              className={`absolute ${editing ? 'cursor-move' : ''} ${chrome}`}
              style={placeStyle(live, scale)}
            >
              <TemplateBox
                box={box}
                field={field}
                missing={box.kind === 'field' && !!box.fieldId && !field}
                mode={mode}
                scale={scale}
                sampleContext={sampleContext}
              />
              {selected &&
                HANDLES.filter((handle) => !compact || handle.length === 2).map((handle) => (
                  // The square is drawn small but caught generously: the
                  // invisible ring around it is what the pointer has to hit.
                  <span
                    key={handle}
                    data-handle={handle}
                    aria-hidden="true"
                    className={`absolute w-2.5 h-2.5 -translate-x-1/2 -translate-y-1/2 rounded-sm bg-white border-2 border-purple-600 after:absolute after:-inset-1.5 after:content-[''] ${HANDLE_PLACES[handle]}`}
                  />
                ))}
            </div>
          );
        })}

        {drawing && (
          <div
            className="absolute pointer-events-none border-2 border-dashed border-purple-500 bg-purple-500/10"
            style={placeStyle(gesture.rect, scale)}
            aria-hidden="true"
          />
        )}
        {dropPreview && (
          <div
            className="absolute pointer-events-none flex items-center overflow-hidden border-2 border-dashed border-purple-500 bg-purple-500/15 px-1"
            style={placeStyle(dropPreview.rect, scale)}
            aria-hidden="true"
          >
            <span className="truncate text-purple-700" style={{ fontSize: 10 * scale }}>
              {dropPreview.title}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateCanvas;
